import { useCallback, useEffect, useRef, useState } from 'react'
import { createId } from '@/features/chat/chatIds'
import { createThread } from '@/services/chat/threadsApi'

/**
 * Voice mode from the home / new-chat screen. A LiveKit room token requires an
 * existing conversation, so we create + persist a thread, make it active, then
 * start the room once the backend has the thread and `displayThreadId` (hence the
 * room's conversationId) has propagated. From there the thread view owns the stop
 * control, so this only ever needs to start.
 * @param {{ m: object, voice: { active: boolean, status: string, start: () => Promise<void>, stop: () => Promise<void> } }} p
 */
export function useHomeVoiceStart({ m, voice }) {
  const voiceRef = useRef(voice)
  const targetRef = useRef(/** @type {string | null} */ (null))
  const [armed, setArmed] = useState(0)
  useEffect(() => { voiceRef.current = voice }, [voice])

  useEffect(() => {
    if (!targetRef.current || m.displayThreadId !== targetRef.current) return
    const v = voiceRef.current
    if (v.active || v.status === 'connecting') return
    targetRef.current = null
    void v.start()
  }, [m.displayThreadId, armed])

  return useCallback(async () => {
    const v = voiceRef.current
    if (v.active || v.status === 'connecting') { void v.stop(); return }
    const threadId = createId()
    const title = 'Voice chat'
    m.setThreads((prev) => [{ id: threadId, title, projectId: m.activeProjectId ?? null }, ...prev])
    m.setSelectedThreadId(threadId)
    try {
      await createThread({ id: threadId, title, projectId: m.activeProjectId })
    } catch {
      // start() surfaces any token/room failure via voice.error
    }
    targetRef.current = threadId
    setArmed((n) => n + 1)
  }, [m])
}
